import React from 'react';
import { Ban, RefreshCw, Zap, Download, Sparkles } from 'lucide-react';

const translations = {
  pt: {
    title: 'Por que escolher',
    brand: 'MundoDigital24',
    question: '?',
    features: [
      {
        icon: Download,
        title: 'Downloads Ilimitados',
        description: 'Baixe quantos jogos quiser, quando quiser. Acesso completo a toda nossa biblioteca de jogos premium sem limites.',
        gradient: 'from-purple-600 to-violet-600',
      },
      {
        icon: Ban,
        title: 'Sem Anúncios',
        description: 'Desfrute de uma experiência de jogo sem interrupções. Nossos jogos premium são totalmente livres de anúncios.',
        gradient: 'from-violet-600 to-fuchsia-600',
      },
      {
        icon: RefreshCw,
        title: 'Atualizações Constantes',
        description: 'Nossa biblioteca é atualizada regularmente com os jogos mais recentes e populares. Sempre há algo novo para descobrir.',
        gradient: 'from-fuchsia-600 to-pink-600',
      },
      {
        icon: Zap,
        title: 'Download Rápido',
        description: 'Baixe seus jogos favoritos em segundos. Interface otimizada para downloads rápidos e instalação fácil.',
        gradient: 'from-pink-600 to-rose-600',
      },
    ],
  },
  en: {
    title: 'Why choose',
    brand: 'MundoDigital24',
    question: '?',
    features: [
      {
        icon: Download,
        title: 'Unlimited Downloads',
        description: 'Download as many games as you want, whenever you want. Full access to our entire library of premium games with no limits.',
        gradient: 'from-purple-600 to-violet-600',
      },
      {
        icon: Ban,
        title: 'No Ads',
        description: 'Enjoy an uninterrupted gaming experience. Our premium games are completely ad-free.',
        gradient: 'from-violet-600 to-fuchsia-600',
      },
      {
        icon: RefreshCw,
        title: 'Regular Updates',
        description: 'Our library is regularly updated with the latest and most popular games. There\'s always something new to discover.',
        gradient: 'from-fuchsia-600 to-pink-600',
      },
      {
        icon: Zap,
        title: 'Fast Downloads',
        description: 'Download your favorite games in seconds. Optimized interface for quick downloads and easy installation.',
        gradient: 'from-pink-600 to-rose-600',
      },
    ],
  }
};

function Features({ language }) {
  const t = translations[language];

  return (
    <section id="features" className="relative py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-darker via-darker to-surface/20"></div>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/50 to-transparent"></div>
      
      {/* Purple orbs */}
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-primary/20 to-secondary/20 border border-primary/30 rounded-full mb-6 backdrop-blur-sm">
            <Sparkles className="w-6 h-6 text-accent" />
            <span className="text-accent text-sm font-body font-bold tracking-wide uppercase">Premium Features</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black text-white mb-6">
            {t.title} <span className="gradient-text">{t.brand}</span>{t.question}
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto rounded-full"></div>
        </div>

        {/* Features Grid - New Card Layout */}
        <div className="grid md:grid-cols-2 gap-8">
          {t.features.map((feature, index) => {
            const IconComponent = feature.icon;
            
            return (
              <div
                key={index}
                className="group relative p-8 rounded-3xl bg-gradient-to-br from-surface/80 to-surface/40 backdrop-blur-xl border border-primary/20 hover:border-primary/40 transition-all duration-300 hover:shadow-purple-glow overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Background gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                
                {/* Content */}
                <div className="relative z-10">
                  {/* Icon */}
                  <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${feature.gradient} mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-4">
                    {feature.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-slate-300 font-body text-lg leading-relaxed">
                    {feature.description}
                  </p>
                </div>

                {/* Decorative corner elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/5 to-transparent rounded-bl-full"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-secondary/5 to-transparent rounded-tr-full"></div>
                
                {/* Hover shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <a
            href="/s"
            className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_100%] hover:bg-right text-white font-body font-bold text-xl rounded-2xl shadow-purple-glow hover:shadow-neon-purple transition-all duration-500"
          >
            <Download className="w-6 h-6" />
            {language === 'pt' ? 'Começar Agora' : 'Get Started Now'}
            <Zap className="w-6 h-6" />
          </a>
        </div>
      </div>
    </section>
  );
}

export default Features;
